// The debug overlay. There is no Safari Web Inspector without a Mac, so this
// panel is the console. Opened by a three-second press in the top-left
// corner, or by loading the page with ?debug=1.
import './overlay.css';
import { config } from '../config.js';
import { el, button } from './controls.js';

export function createOverlay() {
  const root = el('div', { class: 'mw-debug' });
  root.hidden = true;
  document.body.append(root);

  // Toolbar
  const bar = el('div', { class: 'mw-bar' });
  root.append(bar);
  bar.append(button('Close', () => close()));
  bar.append(
    button('Console', async () => {
      const eruda = (await import('eruda')).default;
      eruda.init();
      eruda.show();
    }),
  );
  bar.append(button('Reload', () => location.reload()));
  bar.append(button('Dock', () => root.classList.toggle('right')));

  // Sections and rows
  const sections = new Map();
  const rows = [];

  function section(title) {
    if (sections.has(title)) return sections.get(title);
    const wrap = el('div', {}, [el('h2', { text: title })]);
    root.append(wrap);
    sections.set(title, wrap);
    return wrap;
  }

  function row(sectionTitle, label, provider) {
    const v = el('span', { class: 'v' });
    section(sectionTitle).append(el('div', { class: 'mw-row' }, [el('span', { class: 'k', text: label }), v]));
    rows.push({ v, provider });
  }

  // Log
  const logLines = [];
  const logEl = el('pre', { class: 'mw-log' });
  function renderLog() {
    logEl.replaceChildren(
      ...logLines.map((line) => el('div', { class: line.level === 'error' ? 'err' : '', text: line.text })),
    );
    logEl.scrollTop = logEl.scrollHeight;
  }
  function log(message, level = 'info') {
    const time = new Date().toISOString().slice(11, 19);
    logLines.push({ text: `${time} ${message}`, level });
    while (logLines.length > config.debug.logLines) logLines.shift();
    (level === 'error' ? console.error : console.log)(`[mw] ${message}`);
    if (!root.hidden) renderLog();
  }

  // Refresh at a few Hz while open; rows read live values through providers.
  let lastRefresh = 0;
  function refresh(force = false) {
    if (root.hidden) return;
    const now = performance.now();
    if (!force && now - lastRefresh < 250) return;
    lastRefresh = now;
    for (const { v, provider } of rows) {
      let text;
      try {
        text = provider();
      } catch (err) {
        text = `! ${err.message}`;
      }
      v.textContent = text === undefined || text === null ? '-' : String(text);
    }
  }

  function open() {
    root.hidden = false;
    pill.hidden = true;
    renderLog();
    refresh(true);
  }
  function close() {
    root.hidden = true;
    pill.hidden = true;
  }

  // Minimised: the panel gets out of the way (e.g. while tapping the feed to
  // place perches) and a pill remains; tapping the pill restores the panel.
  const pill = el('button', { class: 'mw-debug-pill', type: 'button' });
  pill.hidden = true;
  let onPillTap = null;
  pill.addEventListener('click', () => {
    onPillTap?.();
    onPillTap = null;
    open();
  });
  document.body.append(pill);
  function minimize(text, onTap) {
    root.hidden = true;
    pill.textContent = text;
    pill.hidden = false;
    onPillTap = onTap ?? null;
  }

  // Hot corner: press and hold for config.debug.holdMs. The click that ends
  // the hold is swallowed so the start screen underneath does not fire.
  let holdTimer = null;
  let holdPointer = null;
  let suppressClick = false;
  const inCorner = (e) => e.clientX <= config.debug.cornerPx && e.clientY <= config.debug.cornerPx;
  const cancelHold = () => {
    if (holdTimer) clearTimeout(holdTimer);
    holdTimer = null;
    holdPointer = null;
  };
  window.addEventListener(
    'pointerdown',
    (e) => {
      if (!root.hidden || !inCorner(e)) return;
      cancelHold();
      holdPointer = e.pointerId;
      holdTimer = setTimeout(() => {
        holdTimer = null;
        suppressClick = true;
        open();
      }, config.debug.holdMs);
    },
    { capture: true },
  );
  for (const type of ['pointerup', 'pointercancel']) {
    window.addEventListener(
      type,
      (e) => {
        if (e.pointerId === holdPointer) cancelHold();
      },
      { capture: true },
    );
  }
  window.addEventListener(
    'pointermove',
    (e) => {
      if (holdTimer && e.pointerId === holdPointer && !inCorner(e)) cancelHold();
    },
    { capture: true },
  );
  window.addEventListener(
    'click',
    (e) => {
      if (!suppressClick) return;
      suppressClick = false;
      e.stopPropagation();
      e.preventDefault();
    },
    { capture: true },
  );

  // Log section sits at the end of whatever sections get registered.
  const logSection = section('Log');
  logSection.append(logEl);
  const keepLogLast = () => root.append(logSection);

  if (new URLSearchParams(location.search).get('debug') === '1') open();

  return {
    root,
    section: (title) => {
      const s = section(title);
      keepLogLast();
      return s;
    },
    row: (sectionTitle, label, provider) => {
      row(sectionTitle, label, provider);
      keepLogLast();
    },
    log,
    open,
    close,
    minimize,
    tick: () => refresh(false),
    isOpen: () => !root.hidden,
  };
}
